import { cn, inputBaseClass, Label } from '@paguz/ui';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { useId, type ComponentProps, type ReactNode } from 'react';

/**
 * Controles que `@paguz/ui` todavía no trae (Textarea, Select, Switch, Checkbox, Field).
 * Locales a la consola; solicitud abierta para moverlos al design system.
 * Versión compacta (40 px) para formularios de escritorio densos.
 */
export const compactInputClass = 'h-10 px-3 text-sm';

export function Textarea({ className, ...props }: ComponentProps<'textarea'>) {
  return (
    <textarea
      className={cn(inputBaseClass, 'h-auto min-h-24 py-2.5 text-sm leading-relaxed', className)}
      {...props}
    />
  );
}

export function Select({ className, children, ...props }: ComponentProps<'select'>) {
  return (
    <div className={cn('relative', className)}>
      <select
        className={cn(inputBaseClass, compactInputClass, 'cursor-pointer appearance-none pr-9')}
        {...props}
      >
        {children}
      </select>
      <ChevronDownIcon
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
      />
    </div>
  );
}

export interface SwitchProps extends Omit<ComponentProps<'button'>, 'onChange'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
}

export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  className,
  id,
  disabled,
  ...props
}: SwitchProps) {
  const generated = useId();
  const switchId = id ?? generated;
  const control = (
    <button
      type="button"
      role="switch"
      id={switchId}
      aria-checked={checked}
      disabled={disabled}
      onClick={() => {
        onCheckedChange(!checked);
      }}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:cursor-not-allowed disabled:opacity-50',
        checked ? 'bg-highlight' : 'bg-slate-300',
      )}
      {...props}
    >
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none block size-5 rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-5' : 'translate-x-0',
        )}
      />
    </button>
  );
  if (!label) return <span className={className}>{control}</span>;
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <Label htmlFor={switchId} className="cursor-pointer">
          {label}
        </Label>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {control}
    </div>
  );
}

export interface CheckboxProps extends Omit<ComponentProps<'input'>, 'type' | 'onChange'> {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
}

export function Checkbox({
  checked,
  onCheckedChange,
  label,
  description,
  className,
  id,
  ...props
}: CheckboxProps) {
  const generated = useId();
  const inputId = id ?? generated;
  return (
    <label
      htmlFor={inputId}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border border-border px-3 py-2.5 transition-colors hover:bg-accent/60 has-[:checked]:border-highlight/50 has-[:checked]:bg-highlight-soft/40',
        className,
      )}
    >
      <span className="relative mt-0.5 inline-flex size-5 shrink-0">
        <input
          id={inputId}
          type="checkbox"
          checked={checked}
          onChange={(event) => {
            onCheckedChange(event.target.checked);
          }}
          className="peer size-5 cursor-pointer appearance-none rounded-md border border-input bg-background outline-none checked:border-highlight checked:bg-highlight focus-visible:ring-[3px] focus-visible:ring-ring/40"
          {...props}
        />
        <CheckIcon
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 m-auto size-3.5 text-highlight-foreground opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="min-w-0 text-sm">
        <span className="font-medium text-foreground">{label}</span>
        {description && <span className="block text-muted-foreground">{description}</span>}
      </span>
    </label>
  );
}

export interface FieldProps {
  label: ReactNode;
  htmlFor?: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: ReactNode;
  className?: string;
}

/** Etiqueta + control + ayuda/error (el error se anuncia con `role="alert"`). */
export function Field({ label, htmlFor, hint, error, children, className }: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p role="alert" className="text-sm font-medium text-destructive">
          {error}
        </p>
      ) : (
        hint && <p className="text-sm text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/** Campo numérico con sufijo (%, días, s…). */
export function SuffixInput({
  suffix,
  className,
  ...props
}: ComponentProps<'input'> & { suffix: ReactNode }) {
  return (
    <div className={cn('relative', className)}>
      <input
        className={cn(inputBaseClass, compactInputClass, 'pr-14 tabular-nums')}
        inputMode="decimal"
        {...props}
      />
      <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-foreground">
        {suffix}
      </span>
    </div>
  );
}
